import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDomainTone } from '../components/FormationCard';
import { StateMessage } from '../components/StateMessage';
import { useFormations } from '../data/queries';
import { useSEO } from '../hooks/useSEO';
import type { Formation, FormationSession } from '../types';

type Entry = { formation: Formation; session: FormationSession };

/**
 * Build a Date from the YYYY-MM-DD portion of an ISO string, in local time.
 * Avoids the same TZ day-rollover issue that affected the per-module date
 * formatter (see `dayOnly` in adapters.ts).
 */
function localDay(iso: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (m) {
    return new Date(
      parseInt(m[1]!, 10),
      parseInt(m[2]!, 10) - 1,
      parseInt(m[3]!, 10),
    );
  }
  return new Date(iso);
}

/** Human label + tone for the inscription status shown on each session row. */
function statusInfo(s: FormationSession): { label: string; tone: 'open' | 'soldout' | 'custom' } {
  if (s.registrationStatus === 'soldout' || s.placesRestantes === 0) {
    return { label: 'Complète', tone: 'soldout' };
  }
  if (s.registrationStatus === 'custom' && s.customStatus) {
    return { label: s.customStatus, tone: 'custom' };
  }
  return { label: 'Inscriptions ouvertes', tone: 'open' };
}

export function Calendrier() {
  const navigate = useNavigate();
  const formationsQ = useFormations();
  const formations = formationsQ.data ?? [];

  useSEO({
    title: 'Calendrier',
    description: 'Toutes les sessions à venir, vue chronologique par mois.',
  });

  // Flatten formations × their upcoming sessions, then group by month.
  const byMonth = useMemo(() => {
    const entries: Entry[] = formations.flatMap((f) =>
      f.upcomingSessions.map((s) => ({ formation: f, session: s })),
    );
    const m: Record<string, { label: string; items: Entry[] }> = {};
    entries.forEach((e) => {
      const d = localDay(e.session.startDate);
      const k = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, '0')}`;
      const lbl = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      const bucket = m[k] ?? { label: lbl, items: [] };
      bucket.items.push(e);
      m[k] = bucket;
    });
    // Sort entries within each month chronologically
    Object.values(m).forEach((bucket) =>
      bucket.items.sort((a, b) => a.session.startDate.localeCompare(b.session.startDate)),
    );
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b));
  }, [formations]);

  return (
    <div className="page-calendrier">
      <div className="cat-head">
        <span className="mtc-eyebrow">Calendrier</span>
        <h1>Toutes les sessions à venir</h1>
        <p>Vue chronologique. Cliquez sur une session pour voir le programme et vous inscrire.</p>
      </div>

      {formationsQ.isLoading ? (
        <StateMessage variant="loading" />
      ) : formationsQ.error ? (
        <StateMessage variant="error" message={String(formationsQ.error)} />
      ) : byMonth.length === 0 ? (
        <div className="cat-empty">
          <p>Aucune session programmée pour le moment.</p>
        </div>
      ) : (
        <div className="cal-timeline">
          {byMonth.map(([k, group]) => (
            <div key={k} className="cal-month">
              <div className="cal-month-label">{group.label}</div>
              <div className="cal-month-items">
                {group.items.map(({ formation: f, session: s }) => {
                  const d = localDay(s.startDate);
                  const tone = getDomainTone(f.domaine);
                  const moduleCount = f.programme.length;
                  const status = statusInfo(s);
                  return (
                    <button
                      key={`${f.id}-${s.id}`}
                      type="button"
                      className="cal-item"
                      onClick={() =>
                        navigate(`/inscription?formation=${f.id}&session=${s.id}`)
                      }
                    >
                      <div className="cal-item-date">
                        <span className="cal-item-day">{d.getDate()}</span>
                        <span className="cal-item-mon">
                          {d.toLocaleDateString('fr-FR', { month: 'short' })}
                        </span>
                      </div>
                      <div className="cal-item-main">
                        <div className="cal-item-meta">
                          <span className={`mtc-pill mtc-pill-${tone}`}>{f.domaine}</span>
                          <span>
                            {moduleCount} module{moduleCount > 1 ? 's' : ''}
                          </span>
                        </div>
                        <h4>{f.titre}</h4>
                        <div className="cal-item-foot">
                          <span>{s.lieu}</span>
                          <span className="mtc-dot">·</span>
                          <span className={`cal-item-status cal-item-status-${status.tone}`}>
                            {status.label}
                          </span>
                          <span className="mtc-dot">·</span>
                          <span>{f.prixParModule.toLocaleString('fr-FR')} MAD HT / module</span>
                        </div>
                      </div>
                      <div className="cal-item-arrow">→</div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
